import taggingData from 'data/FoodTaggingInfo.json';
import { useEffect, useState } from 'react';
import { Label, Card, Checkbox, Message, Button, Input, Icon, Rating } from 'semantic-ui-react';
import type { SemanticCOLORS, StrictCheckboxProps } from 'semantic-ui-react';
import type {
  FoodItem,
  FoodItemRatings,
  FoodItemStarCounts,
  NutritionInfo,
  PatientFoodObservations,
} from 'types';
import { getIncludeMatches, getUniqueElements, removeMatches } from 'utils/utils';

import { VENDORS } from '../../../vendorConfig';
import {
  VendorNutritionInfo,
  VendorAllergensInfo,
  VendorSoupsInfo,
  PatientObservationsInFoodItem,
  PatientAllergiesInFoodItem,
} from '../../../vendorUtils';

// The JSON's keys are literal types, so a computed subscript resolves to an error type.
type TagGroups = Record<string, string[] | undefined>;

/**
 * What one catalog item card accepts.
 *
 * Established from its single JSX call site, `./foodBundleItems.tsx:895`, which
 * passes thirteen props. Twelve are read here; `selectedItemIds` is not — the
 * card tracks its own `checkedItem` state instead, and resets it from
 * `renderFoodItemCardFlag` rather than from the selection the parent holds.
 * Declared so that call site keeps compiling.
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. Because `selectedItemIds` is ignored,
 * a card's checkbox reflects only what this instance has been clicked, not what
 * the parent believes is selected for the member. `foodBundleItems.tsx:902`
 * passes `memberSelectedItemIds[props.patientId]`, so the parent clearly intends
 * per-member selection to survive; whether the cards are meant to rehydrate from
 * it is a product question, and wiring it up would change what the catalog shows.
 *
 * Every optional carries an explicit `| undefined` rather than a bare `?`: under
 * `exactOptionalPropertyTypes` the parent assigns each of these unconditionally, so
 * the property is always PRESENT and sometimes holds `undefined`. `clickedItem` and
 * `observationsData` also admit `null`, which is what the parent's `useState`s hold
 * before anything is selected or the profile bundle is walked.
 */
type FoodItemCardProps = {
  /** The bundle row this card was rendered from; only `quantity` is read. */
  bundleItem?: FoodItem | undefined;
  /** The item currently open in the detail pane; compared by `itemId` to highlight this card. */
  clickedItem?: FoodItem | null | undefined;
  /** Aggregate member feedback for this item. */
  foodRatings?: FoodItemRatings | undefined;
  /** The full catalog record, looked up by `itemId` in the parent's detail map. */
  item?: FoodItem | undefined;
  /** Toggles this item's checkbox; the third argument is the current quantity. */
  onItemCheck?:
    | ((
        event: React.FormEvent<HTMLInputElement>,
        data: StrictCheckboxProps,
        quantity: number,
      ) => void)
    | undefined;
  /** Opens the item detail pane. */
  onItemClick?: ((item?: FoodItem) => void) | undefined;
  /** The member's allergies, avoidances and preferences, used to tag this card. */
  observationsData?: PatientFoodObservations | null | undefined;
  /** Truthy when the vendor's order deadline has passed; disables the controls. */
  orderDeadLine?: unknown;
  patientId?: string | null | undefined;
  /** Changing it resets this card's quantity and checkbox. */
  renderFoodItemCardFlag?: unknown;
  /**
   * Accepted but unread — see the SUSPECTED DEFECT note above. A `Set`, not an
   * array: `foodBundleItems.tsx:34` and the `memberSelectedItemIds` map it is
   * drawn from both hold `Set`s.
   */
  selectedItemIds?: Set<unknown> | undefined;
  /** Reports this card's quantity back to the parent. */
  setItemsQuantity?: ((itemId: string | undefined, quantity: number) => void) | undefined;
  /** A vendor code; selects which `Vendor*Info` parser reads the label fields. */
  vendor?: string | undefined;
};

function FoodItemCard(props: FoodItemCardProps) {
  const tagColors = {
    green: 'teal' as SemanticCOLORS,
    yellow: 'orange' as SemanticCOLORS,
    red: 'red' as SemanticCOLORS,
    blue: 'blue' as SemanticCOLORS,
  };

  const vendor = props.vendor;
  const [quantity, setQuantity] = useState(1);
  const [ratings, _setRatings] = useState<FoodItemRatings>(
    props.foodRatings && props.foodRatings !== undefined ? props.foodRatings : {},
  );
  const [checkedItem, setCheckedItem] = useState(false);
  const [nutritionList, setNutritionList] = useState<NutritionInfo[]>([]);
  const [allergensList, setAllergensList] = useState<string[]>([]);
  const [soupsList, setSoupsList] = useState<string[] | undefined>([]);
  const [haveAllergies, setHaveAllergies] = useState(false);
  // `PatientObservationsInFoodItem` returns `[]`, not `false`, for an item with
  // neither a description nor a `docId` — and `[]` is truthy, so such an item renders
  // the avoidance and preference icons.
  const [haveFoodAvoidances, setHaveFoodAvoidances] = useState<boolean | never[]>(false);
  const [haveFoodPreferences, setHaveFoodPreferences] = useState<boolean | never[]>(false);

  useEffect(() => {
    setQuantity(1);
    setCheckedItem(false);
  }, [props.renderFoodItemCardFlag]);

  useEffect(() => {
    //use ingredients list for alleries/avoidances/preferences
    if (
      props.item &&
      props.item.nutritionInfo &&
      (props.vendor === VENDORS.FF ||
        props.vendor === VENDORS.SB ||
        props.vendor === VENDORS.PK ||
        props.vendor === VENDORS.OK ||
        VENDORS.CJ === props.vendor ||
        VENDORS.EF === props.vendor ||
        props.vendor === VENDORS.EW ||
        props.vendor === VENDORS.FFH ||
        props.vendor === VENDORS.FFSH ||
        props.vendor === VENDORS.NU ||
        props.vendor === VENDORS.MH ||
        props.vendor === VENDORS.ML)
    ) {
      setHaveAllergies(false);
      // For Labels Tagging - Begin
      // `nutritionInfo` values are `number | string`, but every parser in vendorUtils
      // calls `.indexOf` on them: a numeric value here throws.
      setNutritionList(
        VendorNutritionInfo(props.item.nutritionInfo as Record<string, string>, props.vendor),
      );
      let foodItemAllergens = VendorAllergensInfo(props.item.allergens, props.vendor);
      let exemptAllergens: string[] = []; //filter out coconut milk from milk tags
      if (
        props.item.ingredients &&
        props.item.ingredients.length > 0 &&
        props.observationsData?.allergies &&
        props.observationsData?.allergies.length > 0
      ) {
        const commonItems = getIncludeMatches(
          props.item.ingredients,
          props.observationsData.allergies,
        );
        const allergyTags: string[] = props.observationsData.allergies
          //find matching allergens to tags ie: Dairy, Tree Nuts, Fish
          .map((allergen) => {
            let foundTag: string;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the `if` that satisfies the rule is a different expression
            Object.prototype.hasOwnProperty.call(taggingData.allergens, allergen)
              ? (foundTag = allergen)
              : (foundTag = '');
            return foundTag;
          })
          //remove blank entries
          .filter((a) => {
            return a !== '';
          })
          .filter((a: string | undefined) => {
            return a !== undefined;
          });

        if (commonItems && commonItems.length > 0) {
          // find matching patient allergens in the item ingredients and add it to the allergens
          // list (allergen labels)
          foodItemAllergens = [...commonItems, ...foodItemAllergens];
        }
        if (allergyTags && allergyTags.length > 0) {
          //pull entire list of tags from json file
          const allAllergensFromTags: string[] = allergyTags
            .flatMap((a: string) => {
              return (taggingData.allergens as TagGroups)[a]!;
            })
            .filter((a: string | undefined) => {
              return a !== '';
            })
            .filter((a: string | undefined) => {
              return a !== undefined;
            });
          //expempt allergens pull from json file
          exemptAllergens = allergyTags
            .flatMap((a: string) => {
              return (taggingData['non-allergens'] as TagGroups)[a]!;
            })
            .filter((a: string | undefined) => {
              return a !== '';
            })
            .filter((a: string | undefined) => {
              return a !== undefined;
            });
          const ingredients: string[] = props.item.ingredients;
          const falseAllergens: string[] = getIncludeMatches(ingredients, exemptAllergens).map(
            (fa) => fa.toUpperCase(),
          );
          const cleanedIngrediantList = removeMatches(falseAllergens, ingredients);
          const matchingAllergies = getIncludeMatches(cleanedIngrediantList, allAllergensFromTags);

          foodItemAllergens = [...matchingAllergies, ...foodItemAllergens];
        }
      }

      setAllergensList(getUniqueElements(foodItemAllergens));
      setSoupsList(VendorSoupsInfo(props.item, props.vendor));
      // For Labels Tagging - End

      //TODO pass in preferences as array, and parse the macro tags and exclusion tags and pass in array of exclusion allergens
      tagIcons(props.observationsData, props.item, props.vendor, foodItemAllergens);
    }
    // no ingredients list? Use item name
    // Produce Vendors
    else if (
      props.item &&
      props.item.name &&
      (props.vendor === VENDORS.MF || props.vendor === VENDORS.AB || props.vendor === VENDORS.GB)
    ) {
      setHaveAllergies(false);
      let foodItemAllergens: string[] = [];
      let exemptAllergens: string[] = []; //filter out coconut milk from milk tags
      const splitItemName = props.item.name.split(' ');
      const allergyTags: string[] | undefined = props?.observationsData?.allergies
        //find matching allergens to tags ie: Dairy, Tree Nuts, Fish
        ?.map((allergen) => {
          let foundTag: string;
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the `if` that satisfies the rule is a different expression
          Object.prototype.hasOwnProperty.call(taggingData.allergens, allergen)
            ? (foundTag = allergen)
            : (foundTag = '');
          return foundTag;
        })
        //remove blank entries
        ?.filter((a) => {
          return a !== '';
        })
        .filter((a: string | undefined) => {
          return a !== undefined;
        });
      if (
        props.item.name &&
        props.item.name.length > 0 &&
        props.observationsData?.allergies &&
        props.observationsData?.allergies.length > 0
      ) {
        //pull entire list of tags from json file
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const allAllergensFromTags: string[] = allergyTags!.flatMap((a: string) => {
          return (taggingData.allergens as TagGroups)[a]!;
        });
        const commonItems = getIncludeMatches(splitItemName, props.observationsData.allergies);
        if (commonItems && commonItems.length > 0) {
          // find matching patient allergens in the item name and add it to the allergens list
          // (allergen labels)
          foodItemAllergens = [...commonItems, ...foodItemAllergens];
        }
      }

      if (allergyTags && allergyTags.length > 0) {
        //pull entire list of tags from json file
        const allAllergensFromTags: string[] = allergyTags
          .flatMap((a: string) => {
            return (taggingData.allergens as TagGroups)[a]!;
          })
          .filter((a: string | undefined) => {
            return a !== '';
          })
          .filter((a: string | undefined) => {
            return a !== undefined;
          });
        //expempt allergens pull from json file
        exemptAllergens = allergyTags
          .flatMap((a: string) => {
            return (taggingData['non-allergens'] as TagGroups)[a]!;
          })
          .filter((a: string | undefined) => {
            return a !== '';
          })
          .filter((a: string | undefined) => {
            return a !== undefined;
          });
        const falseAllergens: string[] = splitItemName
          ? getIncludeMatches(splitItemName, exemptAllergens, ' ').map((fa) => fa.toUpperCase())
          : (['na'] as string[]);

        const cleanedItemName = removeMatches(falseAllergens, splitItemName, ' ');
        const matchingAllergies = getIncludeMatches(cleanedItemName, allAllergensFromTags);
        foodItemAllergens = [...matchingAllergies, ...foodItemAllergens];
      }
      //if observations Data
      setAllergensList(getUniqueElements(foodItemAllergens));
      tagIcons(props.observationsData, props.item, props.vendor, foodItemAllergens);
    }
  }, [props.item, props.observationsData]);

  // Both call sites sit inside a branch that has already matched `props.vendor`
  // against a `VENDORS` code, so it is a string by the time it reaches here.
  const tagIcons = (
    observationsData: PatientFoodObservations | null | undefined,
    item: FoodItem,
    vendor: string,
    foodItemAllergens: string[],
  ) => {
    // For Icons Tagging - Begin
    if (observationsData) {
      if (observationsData.allergies)
        setHaveAllergies(
          PatientAllergiesInFoodItem(observationsData.allergies, item, vendor, foodItemAllergens),
        );
      if (observationsData.foodPreferences) {
        setHaveFoodPreferences(
          PatientObservationsInFoodItem(item, vendor, observationsData.foodPreferences),
        );
      }
      if (observationsData.foodAvoidance) {
        setHaveFoodAvoidances(
          PatientObservationsInFoodItem(item, vendor, observationsData.foodAvoidance),
        );
      }
    }
    // For Icons Tagging - End
  };

  // SUSPECTED DEFECT — REPORTED, NOT FIXED. Four optional props are used unguarded,
  // each a TS2722 or TS18048: `setItemsQuantity` and `onItemCheck` invoked in the
  // three handlers below, `onItemClick` invoked in the card's `onClick`, and
  // `bundleItem.quantity` read in the quantity Label. A caller omitting any of them
  // raises `TypeError` on first render or first click. The only call site
  // (`foodBundleItems.tsx:895`) passes all four, which is why none has fired.
  // Adding `?.` would silently drop the parent's update instead.
  const increaseQuantity = (item: FoodItem) => {
    props.setItemsQuantity!(item.itemId, quantity + 1);
    setQuantity(quantity + 1);
  };

  const decreaseQuantity = (item: FoodItem) => {
    if (quantity > 1) {
      props.setItemsQuantity!(item.itemId, quantity - 1);
      setQuantity(quantity - 1);
    }
  };

  const handleItemCheck = (
    event: React.FormEvent<HTMLInputElement>,
    data: StrictCheckboxProps,
    quantity: number,
  ) => {
    if (!data.checked) {
      setQuantity(1);
    }
    setCheckedItem(!checkedItem);
    props.onItemCheck!(event, data, quantity);
  };

  /* Calculate ratings*/
  const displayRating = (ratingCount: FoodItemStarCounts | undefined) => {
    if (ratingCount)
      return (
        ((ratingCount.rating1 ? ratingCount.rating1 * 1 : 0) +
          (ratingCount.rating2 ? ratingCount.rating2 * 2 : 0) +
          (ratingCount.rating3 ? ratingCount.rating3 * 3 : 0) +
          (ratingCount.rating4 ? ratingCount.rating4 * 4 : 0) +
          (ratingCount.rating5 ? ratingCount.rating5 * 5 : 0)) /
        ((ratingCount.rating1 ? ratingCount.rating1 : 0) +
          (ratingCount.rating2 ? ratingCount.rating2 : 0) +
          (ratingCount.rating3 ? ratingCount.rating3 : 0) +
          (ratingCount.rating4 ? ratingCount.rating4 : 0) +
          (ratingCount.rating5 ? ratingCount.rating5 : 0))
      );
    else return 0;
  };

  /* Display total reviews count*/
  const displayRatingCount = (ratingCount: FoodItemStarCounts | undefined) => {
    if (ratingCount) {
      return (
        (ratingCount.rating1 ? ratingCount.rating1 : 0) +
        (ratingCount.rating2 ? ratingCount.rating2 : 0) +
        (ratingCount.rating3 ? ratingCount.rating3 : 0) +
        (ratingCount.rating4 ? ratingCount.rating4 : 0) +
        (ratingCount.rating5 ? ratingCount.rating5 : 0)
      );
    } else return 0;
  };

  return (
    <>
      {!props.item && <Message warning header="Info" content="Item Not Found!" />}

      {props.item && (
        <Card
          color="green"
          link={true}
          onClick={() => props.onItemClick!(props.item)}
          fluid
          style={{
            backgroundColor:
              props.clickedItem &&
              props.clickedItem.itemId &&
              props.clickedItem.itemId === props.item.itemId
                ? 'aliceblue'
                : 'white',
            padding: '2px',
            borderRadius: '12px',
            margin: '12px 0px',
            boxShadow: '0 3px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
          }}
        >
          <Card.Content>
            <Card.Header>
              {/*enable check box */}
              {(VENDORS.FF === vendor || VENDORS.SB === vendor || VENDORS.OK === vendor) &&
                props.patientId &&
                !props.orderDeadLine && (
                  <Checkbox
                    checked={checkedItem}
                    id={props.item.itemId + props.patientId}
                    floated="left"
                    onClick={(event, data) => handleItemCheck(event, data, quantity)}
                  />
                )}
              {/*Disable check box */}
              {(VENDORS.PK === vendor ||
                VENDORS.MF === vendor ||
                VENDORS.GB === vendor ||
                VENDORS.AB === vendor ||
                VENDORS.CJ === vendor ||
                VENDORS.MH === vendor ||
                VENDORS.EF === vendor ||
                VENDORS.EW === vendor ||
                VENDORS.ML === vendor ||
                VENDORS.FFH === vendor ||
                VENDORS.FFSH === vendor ||
                VENDORS.SSHE === vendor ||
                VENDORS.NU === vendor ||
                VENDORS.MM === props.vendor ||
                VENDORS.HH === props.vendor ||
                VENDORS.CCF === props.vendor) &&
                props.patientId && (
                  <Checkbox
                    {...(props.item.itemId ? { id: props.item.itemId } : {})}
                    floated="left"
                    disabled
                    checked
                  />
                )}{' '}
              {props.item.name}
              {'  '}
              {haveFoodPreferences && <Icon name="like" color="green" size="small"></Icon>}
              {haveAllergies && <Icon name="bug" color="red" size="small"></Icon>}
              {haveFoodAvoidances && <Icon name="food" color="red" size="small"></Icon>}
            </Card.Header>
            <Card.Meta>
              <div>
                <span style={{ marginRight: '8px' }}>
                  <Icon name="thumbs up" color="teal" /> {ratings.likeCount ? ratings.likeCount : 0}
                </span>
                <span style={{ marginRight: '8px' }}>
                  <Icon name="thumbs down" color="red" />{' '}
                  {ratings.disLikeCount ? ratings.disLikeCount : 0}
                </span>
                <span style={{ marginRight: '8px' }}>
                  <Rating
                    disabled
                    rating={displayRating(ratings.ratingCount)}
                    maxRating={5}
                    style={{ color: 'red' }}
                    icon="star"
                    size="small"
                  />
                </span>
                <span style={{ marginRight: '8px' }}>
                  {displayRatingCount(ratings.ratingCount)} reviews{' '}
                </span>
              </div>
            </Card.Meta>
            {/*Pre-populate item quantity */}
            <Card.Description>
              {(VENDORS.PK === vendor ||
                VENDORS.AB === vendor ||
                VENDORS.CJ === vendor ||
                VENDORS.MF === vendor ||
                VENDORS.GB === vendor ||
                VENDORS.MH === vendor ||
                VENDORS.EF === vendor ||
                VENDORS.EW === vendor ||
                VENDORS.ML === vendor ||
                VENDORS.FFH === vendor ||
                VENDORS.FFSH === vendor ||
                VENDORS.SSHE === vendor ||
                VENDORS.NU === vendor ||
                VENDORS.MM === props.vendor ||
                VENDORS.HH === props.vendor ||
                VENDORS.CCF === props.vendor ||
                !props.patientId) && (
                <Label basic color="black">{`Quantity: ${props.bundleItem!.quantity}`}</Label>
              )}
              {/*choose item quantity */}
              {(VENDORS.FF === vendor || VENDORS.SB === vendor || VENDORS.OK === vendor) &&
                props.patientId &&
                !props.orderDeadLine && (
                  <>
                    <Button
                      disabled={!checkedItem}
                      icon="minus"
                      onClick={() => decreaseQuantity(props.item!)}
                      color="blue"
                      size="mini"
                    />
                    <Input
                      type="text"
                      style={{
                        width: '3.5em',
                        height: '2.1em',
                        textAlign: 'center',
                        marginRight: '3px',
                      }}
                      id="inputQty"
                      readOnly
                      value={quantity}
                      placeholder="Qty"
                    />
                    <Button
                      disabled={!checkedItem}
                      icon="plus"
                      onClick={() => increaseQuantity(props.item!)}
                      color="blue"
                      size="mini"
                    />
                  </>
                )}

              {nutritionList &&
                nutritionList.map((nutrition) => {
                  return (
                    <Label
                      basic
                      key={nutrition.name}
                      {...(nutrition.color ? { color: nutrition.color } : {})}
                    >
                      {nutrition.name}: {nutrition.quantity} {nutrition.unit}
                    </Label>
                  );
                })}
              {allergensList &&
                allergensList.map((allergy) => {
                  return (
                    <Label basic key={allergy} color={tagColors.red}>
                      {allergy}
                    </Label>
                  );
                })}
              {soupsList &&
                soupsList.map((soup) => {
                  return (
                    <Label basic key={soup} color={tagColors.blue}>
                      {soup}
                    </Label>
                  );
                })}
            </Card.Description>
          </Card.Content>
        </Card>
      )}
    </>
  );
}

export default FoodItemCard;
